import React, { SyntheticEvent, useEffect } from "react";
import {
  RadioButtonGroup,
  RadioButtonGroupItem,
} from "@flightright/reusable-components";
import { Box, SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import { FieldProps } from "@/core/components/Field";
import { Controller, useFormContext } from "react-hook-form";
import {
  FieldOrientation,
  HeadingLevel,
  ICON_BINDING_SOURCE,
  IconPosition,
  IconSize,
  MATERIAL_ICONS_USED,
  MaterialIconsPack,
  RadioIconsType,
} from "@/core/types/types";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { TextVariant } from "@/core/components/Field/Text";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

const getRadioButtonGroupStyles = (
  orientation?: FieldOrientation,
  isDesktop: boolean = false
): SxProps<Theme> => {
  const isHorizontal: boolean = orientation === FieldOrientation.Horizontal;
  const isFullWidth: boolean =
    orientation === FieldOrientation.HorizontalFullWidth;
  const isHorizontallyOriented: boolean =
    (isHorizontal || isFullWidth) && isDesktop;

  return {
    display: "flex",
    gap: isHorizontallyOriented ? 2 : 1,
    flexDirection: isHorizontallyOriented ? "row" : "column",
    justifyContent: isFullWidth ? "space-around" : "flex-start",
    "& label": {
      flex: isFullWidth ? 1 : 0,
      minHeight: 48,
    },
  };
};

const DEFAULT_VALUE_PLACEHOLDER = "<none>";

const RadioSelectGroup = ({
  field,
  initialValue,
  onChange = () => {},
}: FieldProps) => {
  const theme: Theme = useTheme();
  const isDesktop: boolean = useMediaQuery(theme.breakpoints.up("sm"));

  const { defaultValue } = field;
  const initialSelection = useDefaultValue(initialValue ?? defaultValue);

  const {
    setValue,
    control,
    formState: { errors },
    clearErrors,
  } = useFormContext();

  const setFormValue = (value: string) => {
    clearErrors(field.key);
    setValue(field.key, value);
    onChange(field.key, value);
  };

  const handleChange = (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    const { value } = target;
    setFormValue(value);
  };

  const { errorMessage, errorTranslationKey } = useFieldError(errors, field);

  const iconBindingName: RadioIconsType | undefined =
    field.properties?.[ICON_BINDING_SOURCE];

  const materialIcons: boolean | undefined =
    field.properties?.[MATERIAL_ICONS_USED];
  const iconPosition: IconPosition | undefined =
    iconBindingName ?? materialIcons ? IconPosition.IconBefore : undefined;
  const radioButtonGroupStyles: SxProps<Theme> = getRadioButtonGroupStyles(
    field.properties?.orientation,
    isDesktop
  );

  useEffect(() => {
    if (initialSelection && initialSelection !== DEFAULT_VALUE_PLACEHOLDER) {
      setFormValue(initialSelection);
    }
  }, [initialSelection]);

  return (
    <Controller
      name={field.key}
      control={control}
      defaultValue={null}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      render={({ field: fieldProps }) => (
        <RadioButtonGroup
          {...fieldProps}
          id={field.key}
          error={errorMessage}
          radioGroupStyles={radioButtonGroupStyles}
          handleChange={handleChange}
          color="secondary"
          key={field.key}
          value={fieldProps.value}
        >
          {field.values?.map((el) => {
            const iconName: string = materialIcons
              ? MaterialIconsPack[el.value as keyof typeof MaterialIconsPack]
              : `${el.value.toLocaleLowerCase()}.svg`;

            const iconSrc: string =
              iconBindingName && !materialIcons
                ? `images/icons/${iconBindingName}/${iconName}`
                : "";

            return (
              <RadioButtonGroupItem
                key={el.value}
                value={el.value}
                onChange={handleChange}
                label={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextVariant
                      iconSrc={iconSrc}
                      text={t(el.label)}
                      variant={HeadingLevel.Body1}
                      iconPosition={iconPosition}
                      iconName={iconName}
                      iconSize={IconSize.Medium}
                      canBeFilled={false}
                    />
                  </Box>
                }
              />
            );
          })}
        </RadioButtonGroup>
      )}
    ></Controller>
  );
};

export default RadioSelectGroup;
